import React from 'react'
import { Box, Link, Typography } from '@mui/material'

import PlatformIcon from '../admin/social/PlatformIcon'

/**
 * Attribution for content that came from a social platform: the platform's
 * mark and name, linked back to the content. YouTube's API terms require this
 * wherever YouTube data is shown; the component is platform-aware so every
 * place that shows a post or a comment uses the same one.
 */

export interface PlatformAttributionProps {
  /** `youtube`, `twitter` (or `x`), `tiktok`, `instagram`, `facebook`; anything else shows a globe. */
  platform: string
  /** The content's own address. With it the mark and name are a link. */
  url?: string
  /** RFC 3339 time the source deleted the content. Adds the "Deleted on …" line. */
  deletedAt?: string
  size?: 'small' | 'medium'
}

const PLATFORM_NAMES: Record<string, string> = {
  youtube: 'YouTube',
  twitter: 'X',
  x: 'X',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const normalizePlatform = (platform: string): string => (platform || '').trim().toLowerCase()

export const platformDisplayName = (platform: string): string => {
  const key = normalizePlatform(platform)
  return PLATFORM_NAMES[key] || (platform ? platform.trim() : 'the platform')
}

/** "d Mon yyyy" in UTC, or '' for a value that is not a date. */
export const formatSourceDate = (value: string): string => {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/**
 * The official YouTube icon (brand.youtube, "Core YouTube icon", Digital, 01 Red),
 * trimmed of its transparent clear space only. YouTube's branding rules forbid
 * redrawing or recolouring it, so it is the downloaded file, not an SVG of ours.
 */
const YOUTUBE_ICON_SRC = '/brand/youtube-icon.png'
const YOUTUBE_ICON_RATIO = 137 / 96

const YouTubeMark: React.FC<{ height: number }> = ({ height }) => (
  <img
    src={YOUTUBE_ICON_SRC}
    height={height}
    width={Math.round(height * YOUTUBE_ICON_RATIO)}
    alt="YouTube logo"
    data-testid="youtube-logo"
    style={{ display: 'block', flexShrink: 0 }}
  />
)

/** What a record shows in place of its text once the source deleted it. */
export const DeletedAtSourceNotice: React.FC<{ platform: string; deletedAt: string }> = ({ platform, deletedAt }) => {
  const date = formatSourceDate(deletedAt)
  return (
    <Typography variant="body2" color="text.secondary" data-testid="deleted-at-source">
      {date
        ? `Deleted on ${platformDisplayName(platform)} on ${date}. Text removed; codes kept.`
        : `Deleted on ${platformDisplayName(platform)}. Text removed; codes kept.`}
    </Typography>
  )
}

const PlatformAttribution: React.FC<PlatformAttributionProps> = ({ platform, url, deletedAt, size = 'small' }) => {
  const key = normalizePlatform(platform)
  const name = platformDisplayName(platform)
  const mark = key === 'youtube' ? <YouTubeMark height={size === 'small' ? 14 : 18} /> : <PlatformIcon platform={key === 'x' ? 'twitter' : key} fontSize={size} />
  const content = (
    <>
      {mark}
      <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: 'inherit' }}>
        {name}
      </Typography>
    </>
  )

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
      {url ? (
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          onClick={e => e.stopPropagation()}
          aria-label={`View on ${name}`}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.primary' }}
        >
          {content}
        </Link>
      ) : (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.primary' }}>{content}</Box>
      )}
      {deletedAt && <DeletedAtSourceNotice platform={platform} deletedAt={deletedAt} />}
    </Box>
  )
}

export default PlatformAttribution
